import {App} from 'vue';
import {Router} from 'vue-router';
import {Dictionary, WebAttributionProperties} from './types';

const trackingEndpoint = '/api/t/event';

function extractWebAttributionProperties(): WebAttributionProperties {
    const paramsToExtract: Array<keyof WebAttributionProperties> = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
        'fbclid',
        'gclid',
    ];

    const searchParams = new URLSearchParams(window.location.search);
    const properties: WebAttributionProperties = {};
    for (const param of paramsToExtract) {
        properties[param] = searchParams.get(param) || null;
    }
    return properties;
}

async function trackEvent(eventName: string, eventProperties: Dictionary = {}, userProperties: Dictionary = {}): Promise<Response | void> {
    try {
        return await fetch(trackingEndpoint, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                event_name: eventName,
                event_properties: eventProperties,
                user_properties: userProperties,
            }),
        });
    } catch (error: unknown) {
        window.captureException(error);
        return Promise.resolve();
    }
}

function trackPageView(): Promise<Response | void> {
    const url = window.location.href.split(window.location.origin)[1];
    const eventProperties = {url};

    const userProperties = {
        ...extractWebAttributionProperties(),
        referrer: document.referrer || null,
    };
    return trackEvent('page_viewed', eventProperties, userProperties);
}

/**
 * Vue 3 plugin to track page views within client side navigation
 *
 * @param router - The router instance of the Vue 3 application the plugin is used on
 *
 * @usage
 *
 * ```ts
 *  import {VuePageViewTracking} from '@malt/tracking';
 *
 *  app.use(VuePageViewTracking, router);
 * ```
 */
const VuePageViewTracking = {
    install: (app: App<Element>, router: Router): void => {
        router.afterEach((to, from, failure) => {
            // We don't want to track page view when the page is reloaded
            // as it's already done in the JSP (but only when navigating client side)
            if (from.path !== '/' && !failure) {
                trackPageView();
            }
        });
    },
};

/**
 * Sends a tracking event to /api/t/event endpoint.
 * Fetch errors are handled within the function.
 *
 * @param eventName - The name / identifier of the event. It should respect snake_case format
 * @param eventProperties - The tracking properties of the event. Each property key should respect snake_case format
 * @returns A Promise response
 *
 * @usage
 *
 * ```ts
 *  import {track} from '@malt/tracking';
 *
 *  function trackSomething() {
 *      track('some_event', {some_property: 'foo'});
 *  }
 * ```
 */
function track(eventName: string, eventProperties?: Dictionary, userProperties?: Dictionary): Promise<Response | void> {
    return trackEvent(eventName, eventProperties, userProperties);
}

export {VuePageViewTracking, trackPageView, track, extractWebAttributionProperties};
